import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FunctionsHttpError } from '@supabase/supabase-js';

export interface QueueItem {
  id: string;
  user_id: string;
  content_type: 'video' | 'image' | 'music' | 'speech';
  priority: number;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  prompt: string;
  settings: Record<string, unknown>;
  result_id: string | null;
  error_message: string | null;
  credits_reserved: number;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface GenerationResult {
  success: boolean;
  // Direct generation result
  id?: string;
  status?: string;
  credits_used?: number;
  // Queue fallback result
  queued?: boolean;
  position?: number;
  estimatedWaitSeconds?: number;
  queueId?: string;
  // Error info
  error?: string;
  required?: number;
  available?: number;
}

// Processing rates for estimated wait calculation
const PROCESSING_RATES = {
  video: 3,
  image: 10,
  music: 5,
  speech: 10,
};

export function useGenerationQueue(userId: string | null) {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Fetch initial queue items
  const fetchQueueItems = useCallback(async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('generation_queue')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['queued', 'processing'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useGenerationQueue] Fetch error:', error);
        return;
      }

      setQueueItems(data as QueueItem[] || []);
    } catch (err) {
      console.error('[useGenerationQueue] Unexpected error:', err);
    }
  }, [userId]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!userId) {
      setQueueItems([]);
      return;
    }

    fetchQueueItems();

    const channel = supabase
      .channel(`queue-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'generation_queue',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          console.log('[useGenerationQueue] Realtime update:', payload);
          
          if (payload.eventType === 'INSERT') {
            const newItem = payload.new as QueueItem;
            setQueueItems(prev => [newItem, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            const updatedItem = payload.new as QueueItem;
            
            // If completed or failed, remove from active queue
            if (['completed', 'failed', 'cancelled'].includes(updatedItem.status)) {
              setQueueItems(prev => prev.filter(item => item.id !== updatedItem.id));
              
              // Show notification for completed items
              if (updatedItem.status === 'completed') {
                toast({
                  title: `${updatedItem.content_type.charAt(0).toUpperCase() + updatedItem.content_type.slice(1)} ready!`,
                  description: 'Your generation has completed.',
                });
              } else if (updatedItem.status === 'failed') {
                toast({
                  title: 'Generation failed',
                  description: updatedItem.error_message || 'An error occurred. Credits have been refunded.',
                  variant: 'destructive',
                });
              }
            } else {
              // Update the item in place
              setQueueItems(prev => 
                prev.map(item => item.id === updatedItem.id ? updatedItem : item)
              );
            }
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as { id: string }).id;
            setQueueItems(prev => prev.filter(item => item.id !== deletedId));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, fetchQueueItems, toast]);

  // Generate content directly - calls edge function, falls back to queue if rate limited
  const generateDirect = useCallback(async (
    contentType: 'video' | 'image' | 'music' | 'speech',
    prompt: string,
    settings: Record<string, unknown>
  ): Promise<GenerationResult> => {
    setIsLoading(true);
    
    try {
      // Map content type to edge function name
      const functionName = `generate-${contentType}`;
      
      // Build request body based on content type
      let body: Record<string, unknown>;
      
      if (contentType === 'speech') {
        body = { text: prompt, settings };
      } else if (contentType === 'music') {
        body = { 
          prompt, 
          customMode: settings.customMode || false,
          instrumental: settings.instrumental || false,
          model: settings.model || 'chirp-v5',
          title: settings.title || '',
          tags: settings.tags || '',
          negativeTags: settings.negativeTags || '',
          lyrics: settings.lyrics || ''
        };
      } else {
        body = { prompt, settings };
      }

      console.log(`[generateDirect] Calling ${functionName} with:`, body);
      
      const { data, error } = await supabase.functions.invoke(functionName, { body });

      if (error) {
        console.error('[generateDirect] Function error:', error);
        
        // For FunctionsHttpError, parse the response body
        if (error instanceof FunctionsHttpError) {
          try {
            const errorBody = await error.context.json();
            console.log('[generateDirect] Error body:', errorBody);
            
            // Check if this is an insufficient credits error
            if (errorBody?.error === 'Insufficient credits') {
              return { 
                success: false, 
                error: 'Insufficient credits',
                required: errorBody.required,
                available: errorBody.available
              };
            }
            
            return { success: false, error: errorBody?.error || error.message };
          } catch (parseError) {
            console.error('[generateDirect] Failed to parse error body:', parseError);
          }
        }
        
        return { success: false, error: error.message };
      }

      // Handle error responses from the edge function
      if (data?.error) {
        // Check for rate limiting - queue instead
        if (data.error.includes('rate limit') || data.error.includes('Rate limit') || 
            data.queued === true) {
          return {
            success: true,
            queued: true,
            position: data.position || 1,
            estimatedWaitSeconds: data.estimatedWaitSeconds,
            queueId: data.queueId
          };
        }
        
        return { 
          success: false, 
          error: data.error,
          required: data.required,
          available: data.available
        };
      }

      // Handle successful direct generation
      // Different edge functions return different structures
      if (contentType === 'image') {
        return {
          success: true,
          id: data.image_id,
          status: data.status || 'pending',
          credits_used: data.credits_used
        };
      } else if (contentType === 'video') {
        return {
          success: true,
          id: data.video_id,
          status: data.status || 'pending',
          credits_used: data.credits_used
        };
      } else if (contentType === 'music') {
        return {
          success: true,
          id: data.musicId,
          status: 'processing',
          credits_used: data.credits_used
        };
      } else if (contentType === 'speech') {
        return {
          success: true,
          id: data.speech?.id,
          status: 'completed',
          credits_used: data.credits_used
        };
      }

      return { success: true, ...data };
    } catch (err) {
      console.error('[generateDirect] Unexpected error:', err);
      return { success: false, error: 'Failed to generate content' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Legacy queue submission (kept for fallback)
  const submitToQueue = useCallback(async (
    contentType: 'video' | 'image' | 'music' | 'speech',
    prompt: string,
    settings: Record<string, unknown>
  ): Promise<GenerationResult> => {
    // Use direct generation instead
    return generateDirect(contentType, prompt, settings);
  }, [generateDirect]);

  // Cancel a queued item
  const cancelQueueItem = useCallback(async (queueId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('generation_queue')
        .update({ status: 'cancelled' })
        .eq('id', queueId)
        .eq('status', 'queued');

      if (error) {
        console.error('[useGenerationQueue] Cancel error:', error);
        toast({
          title: 'Cannot cancel',
          description: 'Item may already be processing.',
          variant: 'destructive',
        });
        return false;
      }

      toast({
        title: 'Cancelled',
        description: 'Generation cancelled. Credits will be refunded.',
      });
      return true;
    } catch (err) {
      console.error('[useGenerationQueue] Unexpected cancel error:', err);
      return false;
    }
  }, [toast]);

  // Calculate estimated wait for a position
  const getEstimatedWait = useCallback((position: number, contentType: 'video' | 'image' | 'music' | 'speech'): string => {
    const rate = PROCESSING_RATES[contentType];
    const runsNeeded = Math.ceil(position / rate);
    const waitSeconds = runsNeeded * 30; // 30 seconds per run
    
    if (waitSeconds < 60) {
      return 'Less than a minute';
    } else if (waitSeconds < 120) {
      return '~1 minute';
    } else {
      const minutes = Math.ceil(waitSeconds / 60);
      return `~${minutes} minutes`;
    }
  }, []);

  // Get items by content type
  const getItemsByType = useCallback((contentType: 'video' | 'image' | 'music' | 'speech') => {
    return queueItems.filter(item => item.content_type === contentType);
  }, [queueItems]);

  return {
    queueItems,
    isLoading,
    generateDirect,
    submitToQueue, // Keep for backwards compatibility, but now calls generateDirect
    cancelQueueItem,
    getEstimatedWait,
    getItemsByType,
    refreshQueue: fetchQueueItems,
  };
}
